/**
 * Робота з наголосами в українській мові.
 *
 * Як зберігається наголос: ударна буква + U+0301 (COMBINING ACUTE ACCENT) одразу
 * після неї. Тобто `чита́ти` — це «чита» + U+0301 + «ти».
 *
 * Чому саме так:
 *  • єдиний спосіб, який зберігається в UTF-8 БД без втрат і який легко знайти
 *    програмно (U+0301 — окремий код, на відміну від апострофа, який неоднозначний);
 *  • KaTeX і браузер не ламають такі комбіновані знаки;
 *  • виглядає правильно в конспекті, на картці й в озвучці.
 *
 * Gemini інструкція вимагає саме цього формату (functions/_shared/prompts.js).
 */

export const COMBINING_ACUTE = '\u0301'
/** Знак наголосу перед словом (U+02C8) — підтримуємо, якщо ним скористується LLM. */
export const STRESS_LEADING = '\u02C8'
/** Розділювачі слів для витягування «слів з наголосом». */
const WORD_BREAK = /[\s,;:!?()[\]{}"'«»/\\—–-]/u

/** Тег, за яким вмикаємо підсвітку й озвучку. */
export const STRESS_TAG = 'наголоси'

const isLetter = (ch) => Boolean(ch) && /[\p{L}]/u.test(ch)

/** Чи є в тексті хоч один наголос. */
export const hasStress = (text) =>
  typeof text === 'string' &&
  (text.includes(COMBINING_ACUTE) || text.includes(STRESS_LEADING))

/**
 * Розбиває текст на шматки, позначаючи ударні букви.
 * Знаки наголосу прибираються — їх вимальовує компонент через `stressed`.
 *
 * @param {string} text
 * @returns {Array<{ text: string, stressed: boolean }>}
 */
export function splitStress(text) {
  const src = String(text ?? '')
  /** @type {Array<{ text: string, stressed: boolean }>} */
  const out = []

  for (let i = 0; i < src.length; i++) {
    const ch = src[i]

    // Знак наголосу перед буквою: ˈа → підсвічуємо «а»
    if (ch === STRESS_LEADING) {
      const next = src[i + 1]
      if (isLetter(next)) {
        out.push({ text: next, stressed: true })
        i++
      }
      continue
    }

    // Буква + U+0301 одразу за нею → підсвічуємо попередній шматок
    if (ch === COMBINING_ACUTE) {
      const prev = out.at(-1)
      if (prev) prev.stressed = true
      continue
    }

    out.push({ text: ch, stressed: false })
  }

  return out
}

/**
 * Витягує слова, у яких є наголошена буква, у нормальному вигляді (без знаків).
 * «чита́ти говори́ти» → ['читати', 'говорити']
 * @returns {string[]}
 */
export function stressedWords(text) {
  const src = String(text ?? '')
  if (!hasStress(src)) return []

  const words = []
  let current = ''
  let marked = false

  const flush = () => {
    if (marked && current) words.push(current)
    current = ''
    marked = false
  }

  for (let i = 0; i < src.length; i++) {
    const ch = src[i]

    if (ch === COMBINING_ACUTE) {
      if (current) marked = true
      continue
    }
    if (ch === STRESS_LEADING) {
      if (isLetter(src[i + 1])) marked = true
      continue
    }
    if (WORD_BREAK.test(ch)) {
      flush()
      continue
    }
    current += ch
  }
  flush()

  return words
}

/** Текст без знаків наголосу — для сумісності; Google TTS готуємо через prepareTtsText. */
export const stripStress = (text) =>
  String(text ?? '')
    .replaceAll(COMBINING_ACUTE, '')
    .replaceAll(STRESS_LEADING, '')

/** Чи має картка тег #наголоси (тоді вмикаємо підсвітку та озвучку). */
export function hasStressTag(tags) {
  if (!tags) return false
  const list = Array.isArray(tags) ? tags : String(tags).split(/[\s,]+/)
  return list.some((t) => String(t).replace(/^#/, '').trim().toLowerCase() === STRESS_TAG)
}
